import "./polyfills";

import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";

import { initDatadog } from "./features/logging/init-datadog";
import { initDatadogRum } from "./features/logging/init-datadog-rum";
import { DEVICE_TOKEN_PREFIX } from "./features/push-notifications/constants";
import { setupSwLogForwarding } from "./features/pwa/service-worker-log-forwarder";
import { ClientCacheProvider } from "./features/spor/style-context";
import { sharedLocalStorage } from "./features/storage/local.client";
import {
  setTwaMessageCommunicationSupported,
  setTwaMessagePort,
} from "./features/twa/message-channel.client";

import { onlineManager } from "@tanstack/react-query";

window.addEventListener("message", (event) => {
  for (const port of event.ports) {
    setTwaMessagePort(port);
    port.onmessage = (portEvent) => {
      if (portEvent.data === "PING") {
        setTwaMessageCommunicationSupported();
        port.postMessage("PONG");
        // Request the device token only if we don't already have one stored —
        // the hook in PushNotificationsModal only mounts after login, so this
        // ensures we get the token on the very first launch before the user
        // has authenticated.
        sharedLocalStorage.deviceToken.get().then((existingToken) => {
          if (!existingToken) {
            port.postMessage("REQUEST_DEVICE_TOKEN");
          }
        });
        return;
      }
      // Persist the device token immediately — the hook listener may not be
      // registered yet if the user hasn't logged in when the message arrives.
      if (
        typeof portEvent.data === "string" &&
        portEvent.data.startsWith(DEVICE_TOKEN_PREFIX)
      ) {
        const token = portEvent.data.slice(DEVICE_TOKEN_PREFIX.length);
        if (token) {
          sharedLocalStorage.deviceToken
            .set(token)
            .then(() => console.debug("[TWA] deviceToken stored ✓"))
            .catch((err) =>
              console.error("[TWA] deviceToken storage error", err),
            );
        }
      }
      // Forward to window so hooks can react (e.g. stop polling).
      window.dispatchEvent(
        new CustomEvent("twa-message", { detail: portEvent.data }),
      );
    };
  }
});

onlineManager.setOnline(window.navigator.onLine);

initDatadog();
initDatadogRum();

function hydrate() {
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <ClientCacheProvider>
          <HydratedRouter />
        </ClientCacheProvider>
      </StrictMode>,
    );
  });
}

if (typeof requestIdleCallback === "function") {
  requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1);
}

// Set up listeners for logs from service workers.
setupSwLogForwarding();
